import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, Clock, AlertTriangle } from "lucide-react";

const DEV_LOGIN_ENABLED = process.env.REACT_APP_ENABLE_DEV_LOGIN === "true";

const GOOGLE_ERROR_MESSAGES = {
  access_denied: "Google sign-in was cancelled.",
  missing_code: "Google sign-in didn't complete — please try again.",
  invalid_state: "That sign-in link expired — please try again.",
  not_configured: "Google sign-in isn't set up yet — contact your administrator.",
  token_exchange: "Couldn't complete Google sign-in — please try again.",
  userinfo: "Couldn't read your Google profile — please try again.",
  unverified_email: "Your Google account's email isn't verified.",
  disabled: "Your account has been disabled — contact your administrator.",
};

export default function LoginPage() {
  const { loginWithGoogle, loginWithPassword, devLogin, pendingMessage, setPendingMessage } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (searchParams.get("pending")) {
      setPendingMessage("Your Google account is not provisioned for SalesOS yet. An administrator must approve your access.");
    } else if (searchParams.get("error")) {
      setError(GOOGLE_ERROR_MESSAGES[searchParams.get("error")] || "Sign-in failed — please try again.");
    }
  }, [searchParams, setPendingMessage]);

  const handleGoogleLogin = async () => {
    setError("");
    const r = await loginWithGoogle();
    if (!r.ok) setError(r.detail);
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const r = await loginWithPassword(email, password);
    setBusy(false);
    if (r.ok) navigate("/", { replace: true });
    else setError(r.detail);
  };

  const handleDevLogin = async () => {
    const r = await devLogin();
    if (r.ok) navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--muted))]/40 px-4">
      <Card className="w-full max-w-sm shadow-sm">
        <CardContent className="pt-8 pb-8 flex flex-col items-center gap-5">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">Aussie Pathways</h1>
            <Badge variant="outline" className="mt-1 font-mono text-[10px]">SalesOS</Badge>
            <p className="text-sm text-muted-foreground mt-3">
              Sign in to your account. Access is provisioned by your administrator.
            </p>
          </div>
          {pendingMessage && (
            <Alert data-testid="login-pending-alert" className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10">
              <Clock className="h-4 w-4" />
              <AlertTitle>Access pending</AlertTitle>
              <AlertDescription className="text-xs">{pendingMessage}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert data-testid="login-error-alert" variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handlePasswordLogin} className="w-full space-y-3">
            <div className="space-y-1.5 text-left">
              <Label htmlFor="login-email" className="text-xs">Email</Label>
              <Input id="login-email" data-testid="login-email-input" type="email" required autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5 text-left">
              <Label htmlFor="login-password" className="text-xs">Password</Label>
              <Input id="login-password" data-testid="login-password-input" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <Button data-testid="password-login-button" type="submit" className="w-full h-10" disabled={busy}>
              {busy ? "Signing in…" : "Log in"}
            </Button>
          </form>
          <div className="w-full flex items-center gap-2">
            <Separator className="flex-1" />
            <span className="text-[11px] text-muted-foreground">or</span>
            <Separator className="flex-1" />
          </div>
          <Button data-testid="google-signin-button" variant="outline" className="w-full h-10" onClick={handleGoogleLogin}>
            <ShieldCheck className="h-4 w-4 mr-2" />
            Sign in with Google
          </Button>
          {DEV_LOGIN_ENABLED && (
            <Button
              data-testid="dev-login-button"
              variant="outline"
              className="w-full h-10 border-dashed"
              onClick={handleDevLogin}
            >
              Dev login (local only)
            </Button>
          )}
          <p className="text-[11px] text-muted-foreground text-center">
            Your SalesOS role determines what you can do, regardless of how you sign in.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
